import {useNavigate} from 'react-router-dom'
import {useEffect, useMemo, useState} from 'react'
import Select from 'react-select'
import {ISelect} from '../../../coreGlobal/models/default'
import {
  getFranchisesSelect,
  getBillingEntitiesSelect,
  getPaymentOptionsSelect,
} from '../core/_request'

type LinkType = 'franchise' | 'billing_entity'

export const CieloMerchantForm = ({formik}) => {
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const [franchiseOptions, setFranchiseOptions] = useState<ISelect[]>([])
  const [billingEntityOptions, setBillingEntityOptions] = useState<ISelect[]>([])
  const [paymentOptionOptions, setPaymentOptionOptions] = useState<ISelect[]>([])

  const initialLinkType: LinkType = formik.values.billing_entity_id ? 'billing_entity' : 'franchise'
  const [linkType, setLinkType] = useState<LinkType>(initialLinkType)

  useEffect(() => {
    async function init() {
      const [franchises, billingEntities, paymentOptions] = await Promise.all([
        getFranchisesSelect(),
        getBillingEntitiesSelect(),
        getPaymentOptionsSelect(),
      ])
      setFranchiseOptions(franchises)
      setBillingEntityOptions(billingEntities)
      setPaymentOptionOptions(paymentOptions)
    }
    init()
  }, [])

  const handleLinkTypeChange = (type: LinkType) => {
    setLinkType(type)
    formik.setFieldValue('franchise_id', null)
    formik.setFieldValue('billing_entity_id', null)
  }

  const selectedFranchise = useMemo(
    () =>
      franchiseOptions.find((option) => Number(option.value) === Number(formik.values.franchise_id)) ??
      null,
    [franchiseOptions, formik.values.franchise_id]
  )
  const selectedBillingEntity = useMemo(
    () =>
      billingEntityOptions.find(
        (option) => Number(option.value) === Number(formik.values.billing_entity_id)
      ) ?? null,
    [billingEntityOptions, formik.values.billing_entity_id]
  )
  const selectedPaymentOption = useMemo(
    () =>
      paymentOptionOptions.find(
        (option) => Number(option.value) === Number(formik.values.payment_option_id)
      ) ?? null,
    [paymentOptionOptions, formik.values.payment_option_id]
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className='card mb-5 mb-xl-10'>
        <div className='card-body border-top p-9'>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Nome</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid'
                placeholder='Nome do Merchant'
                {...formik.getFieldProps('name')}
              />
              {formik.touched.name && formik.errors.name && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors?.name}</div>
                </div>
              )}
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span>EC</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder='EC'
                  {...formik.getFieldProps('ec')}
              />
              {formik.touched.ec && formik.errors.ec && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors?.ec}</div>
                  </div>
              )}
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Merchant ID</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid'
                placeholder='Merchant ID'
                {...formik.getFieldProps('merchant_id')}
              />
              {formik.touched.merchant_id && formik.errors.merchant_id && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors?.merchant_id}</div>
                </div>
              )}
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Merchant Key</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid'
                placeholder='Merchant Key'
                {...formik.getFieldProps('merchant_key')}
              />
              {formik.touched.merchant_key && formik.errors.merchant_key && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors?.merchant_key}</div>
                </div>
              )}
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span>Data Inicio</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <input
                type='date'
                className='form-control form-control-lg form-control-solid'
                {...formik.getFieldProps('start_date')}
              />
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span>Data de expiração</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <input
                type='date'
                className='form-control form-control-lg form-control-solid'
                {...formik.getFieldProps('expiration_at')}
              />
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span>Tipo de vínculo</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <div className='d-flex align-items-center'>
                {(
                  [
                    {value: 'franchise', label: 'Franquia'},
                    {value: 'billing_entity', label: 'Entidade de Cobrança'},
                  ] as {value: LinkType; label: string}[]
                ).map((option) => (
                  <div className='form-check form-check-custom form-check-solid me-5' key={option.value}>
                    <input
                      className='form-check-input'
                      type='radio'
                      name='linkType'
                      id={`linkType-${option.value}`}
                      checked={linkType === option.value}
                      onChange={() => handleLinkTypeChange(option.value)}
                    />
                    <label className='form-check-label' htmlFor={`linkType-${option.value}`}>
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {linkType === 'franchise' && (
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span>Franquia</span>
              </label>
              <div className='col-lg-8 fv-row'>
                <Select
                  className='react-select-styled react-select-solid'
                  classNamePrefix='react-select'
                  options={franchiseOptions}
                  value={selectedFranchise}
                  onChange={(option) =>
                    formik.setFieldValue('franchise_id', option ? Number(option.value) : null)
                  }
                  placeholder='Selecione'
                />
              </div>
            </div>
          )}
          {linkType === 'billing_entity' && (
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span>Entidade de Cobrança</span>
              </label>
              <div className='col-lg-8 fv-row'>
                <Select
                  className='react-select-styled react-select-solid'
                  classNamePrefix='react-select'
                  options={billingEntityOptions}
                  value={selectedBillingEntity}
                  onChange={(option) =>
                    formik.setFieldValue('billing_entity_id', option ? Number(option.value) : null)
                  }
                  placeholder='Selecione'
                />
              </div>
            </div>
          )}
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span>Opção de pagamento</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <Select
                className='react-select-styled react-select-solid'
                classNamePrefix='react-select'
                options={paymentOptionOptions}
                value={selectedPaymentOption}
                onChange={(option) =>
                  formik.setFieldValue('payment_option_id', option ? Number(option.value) : null)
                }
                placeholder='Selecione'
              />
            </div>
          </div>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span>Ativo</span>
            </label>
            <div className='col-lg-8 fv-row'>
              <div className='form-check form-switch form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={!!formik.values.active}
                  onChange={(e) => formik.setFieldValue('active', e.target.checked)}
                />
              </div>
            </div>
          </div>
        </div>
        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button type='reset' className='btn btn-secondary me-2' onClick={() => navigate(-1)}>
            Cancelar
          </button>
          <button type='submit' className='btn btn-danger' disabled={loading || !formik.isValid}>
            {!loading && 'Salvar'}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Salvando...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
    </form>
  )
}
